import { useContext, useState } from "react"
import { Button, Col, Div, Row, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBookOpen, faBookmark as faBookmarkSolid } from "@fortawesome/free-solid-svg-icons"
import { faBookmark } from "@fortawesome/free-regular-svg-icons"
import { toast } from "react-toastify"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import Container from "../atoms/Container"
import LessonContentFields from "../lesson/LessonContentFields"
import LessonSummary from "../lesson/LessonSummary"
import NotebookChanges from "../notebook/NotebookChanges"
import NotebookPicker from "../notebook/NotebookPicker"
import NotebookSaveForm from "../notebook/NotebookSaveForm"
import NotebookUpdate from "../notebook/NotebookUpdate"
import { theme } from "../settings/config"
import { notebookContext } from "../providers/notebook.provider"
import { profileContext } from "../providers/profile.provider"
import {
	addNotebookEntry,
	editLessonContent,
	editNotebookEntry,
	recordNotebookUses,
} from "../utils/firestore.utils"
import { isProgramEmpty, normalizeLessonContent } from "../utils/lessonContent.utils"
import { networkAwareMessage } from "../utils/network.utils"
import {
	CHANGE_COPY,
	CHANGE_NEW_VARIANT,
	CHANGE_UPDATE,
	NOTEBOOK_EXERCISE,
	NOTEBOOK_LESSON,
	changedFields,
	copyOfEntry,
	exerciseChanged,
	lessonNotebookLevel,
	lessonSource,
	levelLabel,
	notebookChanges,
	notebookUpdate,
	phaseSchemas,
	phaseSources,
	suggestedExerciseTitle,
	suggestedLessonTitle,
	usedEntryIDs,
	variantOf,
	withChangeSaved,
	withExercise,
	withNewVariant,
	withNotebookLesson,
	withNotebookUpdate,
	withSavedExercise,
	withVariant,
} from "../utils/notebook.utils"

// Un tasto tondo con un'icona, leggero: accanto a una fase o a un esercizio
const IconButton = ({ icon, label, onClick, disabled, color = theme.colors.text_light }) => (
	<Button
		h="2.25rem"
		w="2.25rem"
		p="0"
		bg="transparent"
		aria-label={label}
		title={label}
		disabled={disabled}
		disableBg="transparent"
		onClick={onClick}>
		<FontAwesomeIcon icon={icon} color={color} />
	</Button>
)

// Il programma di una lezione: si apre dalla lezione aperta, non dal
// modulo di creazione, che resta su quando, dove e chi. Le fasi si
// scrivono prima, come piano, o dopo, come diario: sono tutte facoltative.
//
// E il quaderno (src/utils/notebook.utils.js), per non riscrivere ogni
// volta quello che si è già scritto:
// - in cima, la lezione del quaderno da cui si parte, con "Cambia";
// - su ogni fase, prendere un esercizio dal quaderno; su ogni esercizio,
//   salvarlo nel quaderno. Un esercizio preso dal quaderno lo dice, e se lo
//   si cambia dice anche quello;
// - in fondo, "Salva nel quaderno" per il programma intero.
//
// Quello che si prende dal quaderno si copia: cambiare il quaderno dopo non
// cambia questa lezione. Un esercizio che nel quaderno è andato avanti lo
// dice, e **Aggiorna** lo riprende com'è adesso (NotebookUpdate). Il quaderno
// conta un uso quando la lezione si salva con dentro quello che si è preso.
//
// Se quello che si è preso dal quaderno è stato cambiato, Salva prima chiede
// cosa farne (NotebookChanges): solo per questa lezione, aggiornare la
// variante, farne una per un altro livello o, di un collega, una copia. Si
// parte dal livello della lezione: nel quaderno, salvando, e nelle varianti.
export function LessonContentModal({ closeModal, lessonData }) {
	const { getEntry } = useContext(notebookContext)
	const { coach } = useContext(profileContext)
	const lessonLevel = lessonNotebookLevel(lessonData)

	const [content, setContent] = useState(() =>
		normalizeLessonContent(lessonData.lessonContent),
	)
	const [isSaving, setSaving] = useState(false)
	// le voci del quaderno prese o salvate mentre il foglio è aperto
	const [pickedIDs, setPickedIDs] = useState([])
	// cosa c'è al posto del programma: una scelta dal quaderno o un salvataggio
	const [view, setView] = useState(null)

	const pick = entryID => setPickedIDs(ids => [...ids, entryID])
	const back = () => setView(null)

	// `copies`: le voci appena create salvando, che il quaderno non ha ancora
	// riletto, e il loro uso si conta lo stesso
	const save = async (normalized, copies = []) => {
		setSaving(true)
		try {
			await editLessonContent(lessonData.lessonID, normalized)
		} catch {
			toast.error("Il programma della lezione non è stato salvato", {
				position: "top-center",
			})
			setSaving(false)
			return
		}
		// dopo, e senza aspettarlo: la lezione è salvata comunque
		recordNotebookUses(
			usedEntryIDs([...pickedIDs, ...copies.map(copy => copy.entryID)], normalized)
				.map(entryID => getEntry(entryID) ?? copies.find(copy => copy.entryID === entryID))
				.filter(Boolean),
			lessonData.lessonStart,
		)
		closeModal()
	}

	const handleSave = () => {
		// senza gli esercizi lasciati vuoti e gli spazi intorno
		const normalized = normalizeLessonContent(content)
		// Cosa è cambiato si fissa qui, quando si chiede: mentre si salva il
		// quaderno si rilegge (anche subito, con la scrittura appena fatta), e
		// ricalcolato daccapo non direbbe più niente. Il foglio resterebbe
		// senza niente da chiedere, e l'app si fermava.
		const changes = notebookChanges(normalized, getEntry)
		if (changes.length > 0) setView({ type: "changes", changes })
		else save(normalized)
	}

	// Le scelte del foglio "Hai cambiato": prima gli esercizi, così la
	// lezione, se si aggiorna, si porta dietro gli esercizi già aggiornati.
	// Una voce cambiata due volte (due esercizi della stessa) si riscrive
	// partendo da quello appena scritto, non da quello letto prima.
	const saveWithChanges = async (changes, choices) => {
		setSaving(true)
		let next = normalizeLessonContent(content)
		const ordered = [
			...changes.filter(change => change.kind === NOTEBOOK_EXERCISE),
			...changes.filter(change => change.kind === NOTEBOOK_LESSON),
		]
		const written = {}
		const copies = []
		try {
			for (const change of ordered) {
				const choice = choices[change.key]
				if (![CHANGE_UPDATE, CHANGE_NEW_VARIANT, CHANGE_COPY].includes(choice?.type)) continue
				const fields = changedFields(change, next)
				const stored = written[change.entry.entryID] ?? getEntry(change.entry.entryID)
				// tolta nel frattempo, da un altro telefono: resta solo per questa lezione
				if (!stored) continue
				if (choice.type === CHANGE_COPY) {
					const copy = copyOfEntry(stored, change.entry.variantID, fields, choice.title)
					const { entryID } = await addNotebookEntry(coach.coachID, copy)
					copies.push({ entryID, isMine: true, lastUsedAt: null })
					next = withChangeSaved(next, change, {
						...change.entry,
						...fields,
						entryID,
						title: copy.title.trim(),
					})
					continue
				}
				const variants =
					choice.type === CHANGE_UPDATE
						? withVariant(stored, change.entry.variantID, fields)
						: withNewVariant(stored, { ...fields, level: choice.level })
				const savedVariant =
					choice.type === CHANGE_UPDATE
						? variants.find(variant => variant.variantID === change.entry.variantID)
						: variants[variants.length - 1]
				await editNotebookEntry(stored.entryID, {
					kind: stored.kind,
					title: stored.title,
					phase: stored.phase,
					variants,
				})
				written[stored.entryID] = { ...stored, variants }
				next = withChangeSaved(next, change, { ...change.entry, ...savedVariant })
			}
		} catch (error) {
			const message = networkAwareMessage(error, "Il quaderno non è stato aggiornato")
			toast.error(message, { toastId: message, position: "top-center" })
			setSaving(false)
			return
		}
		await save(next, copies)
	}

	if (view?.type === "changes")
		return (
			<Container>
				<NotebookChanges
					changes={view.changes}
					lessonLevel={lessonLevel}
					busy={isSaving}
					onConfirm={choices => saveWithChanges(view.changes, choices)}
					onBack={back}
				/>
			</Container>
		)

	if (view?.type === "pickLesson")
		return (
			<Container>
				<NotebookPicker
					kind={NOTEBOOK_LESSON}
					level={lessonLevel}
					caption={
						isProgramEmpty(content) ? null : "Prende il posto del programma scritto qui."
					}
					onPick={entry => {
						// le note di com'è andata restano: sono della lezione
						setContent(current => withNotebookLesson(current, entry))
						pick(entry.entryID)
						back()
					}}
					onBack={back}
				/>
			</Container>
		)

	if (view?.type === "pickExercise")
		return (
			<Container>
				<NotebookPicker
					kind={NOTEBOOK_EXERCISE}
					phaseID={view.phaseID}
					level={lessonLevel}
					caption="Si aggiunge in fondo agli esercizi della fase."
					onPick={entry => {
						setContent(current => withExercise(current, view.phaseID, entry))
						pick(entry.entryID)
						back()
					}}
					onBack={back}
				/>
			</Container>
		)

	if (view?.type === "saveExercise") {
		const text = content.phases[view.phaseID].exercises[view.index]
		const schema = phaseSchemas(content.phases[view.phaseID])[view.index]
		return (
			<Container>
				<NotebookSaveForm
					kind={NOTEBOOK_EXERCISE}
					phaseID={view.phaseID}
					exerciseText={text}
					exerciseSchema={schema}
					initialTitle={suggestedExerciseTitle(text)}
					initialLevel={lessonLevel}
					onSaved={entry => {
						setContent(current =>
							withSavedExercise(current, view.phaseID, view.index, entry),
						)
						pick(entry.entryID)
						back()
					}}
					onBack={back}
				/>
			</Container>
		)
	}

	if (view?.type === "saveLesson")
		return (
			<Container>
				<NotebookSaveForm
					kind={NOTEBOOK_LESSON}
					content={content}
					initialTitle={suggestedLessonTitle(content)}
					initialLevel={lessonLevel}
					onSaved={entry => {
						setContent(current => ({ ...current, fromNotebook: lessonSource(entry) }))
						pick(entry.entryID)
						back()
					}}
					onBack={back}
				/>
			</Container>
		)

	// " · 3.0", il livello della variante da cui viene, se ce l'ha
	const sourceLevel = source => {
		const level = variantOf(getEntry(source.entryID), source.variantID)?.level
		return level ? ` · ${levelLabel(level)}` : ""
	}

	const phaseAction = phase => (
		<IconButton
			icon={faBookOpen}
			color={theme.colors.gunmetal}
			label={`Prendi un esercizio dal quaderno: ${phase.label.toLowerCase()}`}
			disabled={isSaving}
			onClick={() => setView({ type: "pickExercise", phaseID: phase.id })}
		/>
	)

	const exerciseAction = (phase, index) => {
		const text = content.phases[phase.id].exercises[index]
		const source = phaseSources(content.phases[phase.id])[index]
		const schema = phaseSchemas(content.phases[phase.id])[index]
		// com'è nel quaderno, è già nel quaderno
		const isSaved = Boolean(source) && !exerciseChanged(source, text, schema)
		const name = `${index + 1}: ${phase.label.toLowerCase()}`
		return (
			<IconButton
				icon={isSaved ? faBookmarkSolid : faBookmark}
				color={isSaved ? theme.colors.gunmetal : theme.colors.text_light}
				label={
					isSaved
						? `Già nel quaderno: esercizio ${name}`
						: `Salva nel quaderno l'esercizio ${name}`
				}
				disabled={isSaving || isSaved || text.trim() === ""}
				onClick={() => setView({ type: "saveExercise", phaseID: phase.id, index })}
			/>
		)
	}

	const exerciseNote = (phase, index) => {
		const source = phaseSources(content.phases[phase.id])[index]
		if (!source) return null
		const text = content.phases[phase.id].exercises[index]
		const schema = phaseSchemas(content.phases[phase.id])[index]
		const changed = exerciseChanged(source, text, schema)
		// nel quaderno è andato avanti: si può riprendere com'è adesso
		const update = notebookUpdate(source, text, schema, getEntry)
		return (
			<>
				<Text
					tag="span"
					textSize="tiny"
					textWeight="600"
					textColor={theme.colors.gunmetal}
					bg={theme.colors.gunmetal_lighter}
					rounded="10px"
					p="0.125rem 0.5rem"
					m={{ l: "1.125rem" }}
					style={{ alignSelf: "flex-start" }}>
					Dal quaderno: «{source.title}»{sourceLevel(source)}
					{changed ? " · cambiato" : ""}
				</Text>
				{update && (
					<NotebookUpdate
						update={update}
						name={`esercizio ${index + 1}, ${phase.label.toLowerCase()}`}
						disabled={isSaving}
						onUpdate={() =>
							setContent(current =>
								withNotebookUpdate(current, phase.id, index, update.entry),
							)
						}
					/>
				)}
			</>
		)
	}

	return (
		<>
			<Container>
				<Div textWeight="bold" textSize="title" textColor={theme.colors.text}>
					Programma della lezione
				</Div>
				<Div m={{ b: "0.75rem" }}>
					<LessonSummary lessonData={lessonData} />
				</Div>
			</Container>
			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
					{content.fromNotebook ? (
						<Div
							bg={theme.colors.gunmetal_fill}
							rounded="15px"
							p={{ l: "0.875rem", r: "0.5rem", y: "0.5rem" }}
							m={{ b: "1rem" }}
							d="flex"
							align="center"
							style={{ gap: "0.75rem" }}>
							<FontAwesomeIcon icon={faBookOpen} color={theme.colors.white} />
							<Div flexGrow="1" style={{ minWidth: 0 }}>
								<Text
									textSize="caption"
									textColor={theme.colors.white}
									style={{ opacity: 0.8 }}>
									Dal quaderno
								</Text>
								<Text textWeight="600" textColor={theme.colors.white}>
									«{content.fromNotebook.title}»{sourceLevel(content.fromNotebook)}
								</Text>
							</Div>
							<Button
								h="2.75rem"
								p={{ x: "0.5rem" }}
								bg="transparent"
								textColor={theme.colors.white}
								textWeight="600"
								style={{ textDecoration: "underline" }}
								aria-label="Cambia la lezione del quaderno"
								disabled={isSaving}
								onClick={() => setView({ type: "pickLesson" })}>
								Cambia
							</Button>
						</Div>
					) : (
						<Button
							w="100%"
							h="2.75rem"
							m={{ b: "1rem" }}
							bg={theme.colors.surface}
							rounded="15px"
							textColor={theme.colors.gunmetal}
							style={{ gap: "0.5rem" }}
							disabled={isSaving}
							onClick={() => setView({ type: "pickLesson" })}>
							<FontAwesomeIcon icon={faBookOpen} />
							<Text tag="span" textWeight="600">
								Prendi una lezione dal quaderno
							</Text>
						</Button>
					)}

					<LessonContentFields
						content={content}
						setContent={setContent}
						disabled={isSaving}
						phaseAction={phaseAction}
						exerciseAction={exerciseAction}
						exerciseNote={exerciseNote}
					/>

					<Button
						w="100%"
						h="3rem"
						m={{ t: "1.25rem", b: "0.25rem" }}
						bg="transparent"
						rounded="20px"
						border="2px solid"
						borderColor={theme.colors.gunmetal}
						textColor={theme.colors.gunmetal}
						textWeight="600"
						disableBg="transparent"
						disableColor={theme.colors.text_light}
						style={{ gap: "0.5rem" }}
						disabled={isSaving || isProgramEmpty(content)}
						onClick={() => setView({ type: "saveLesson" })}>
						<FontAwesomeIcon icon={faBookmark} />
						Salva nel quaderno
					</Button>
				</Container>
			</Div>
			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							text="Annulla"
							textSize="subheader"
							disabled={isSaving}
							action={closeModal}
						/>
					</Col>
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.primary}
							textColor="white"
							textSize="subheader"
							text={isSaving ? "Salvo..." : "Salva"}
							disabled={isSaving}
							action={handleSave}
						/>
					</Col>
				</Row>
			</Div>
		</>
	)
}

export default LessonContentModal
